// Return label links in a customer reply: one block per box, and the eBay
// print line (2026-10-27, docs/pipeline-behaviors.md "eBay return labels:
// attached on every path, one label per box";
// docs/decisions/2026-10-27-ebay-label-attach-per-box.md).
//
// - More than one label for the same return: one block per box, in order,
//   each where the template's link placeholder goes:
//     Box 1 of 2:
//     <link>
//     Box 2 of 2:
//     <link>
//   The template's other lines appear once. One label: the link alone, as
//   before.
// - The eBay print line: when the label is attached to the buyer's eBay
//   return, code adds EBAY_PRINT_LINE after the label link(s). When it is not
//   (eBay refused the upload, or there is no eBay return) the line is left
//   out, and a line added before Process is removed if the attach then
//   failed (ebayAttachFailedIn()). The template text itself is never changed.
//
// Pure: no calls. Used by the server (actions.js, action-executor.js,
// pipeline.js, agent-label-link.js, flow-automation.js) and the panel
// (processAll.js, SuggestionPanel.jsx).

export const EBAY_PRINT_LINE = 'You can also print it from the return in your eBay account.'

const isHtml = (text) => /<[a-z][^>]*>/i.test(String(text ?? ''))

// "Box 1 of 2:" for box i (0-based) of n.
export const boxHeading = (i, n) => `Box ${i + 1} of ${n}:`

// The links as the text that replaces the template's link placeholder: one
// link alone; more than one as one block per box. joiner '\n' (plain text)
// or '<br>' (an HTML reply).
export function labelLinkBlocks(urls = [], { joiner = '\n' } = {}) {
  const list = (urls ?? []).filter(Boolean)
  if (list.length <= 1) return list.join(joiner)
  return list.map((u, i) => `${boxHeading(i, list.length)}${joiner}${u}`).join(joiner)
}

// A template's content with its INSERTLABELLINK filled by the links (box
// blocks when there is more than one). Unchanged when there are none.
export function fillLabelLinks(content, urls = [], token = 'INSERTLABELLINK') {
  const text = String(content ?? '')
  const list = (urls ?? []).filter(Boolean)
  if (!list.length || !text.includes(token)) return text
  return text.split(token).join(labelLinkBlocks(list, { joiner: isHtml(text) ? '<br>' : '\n' }))
}

export function hasEbayPrintLine(text) {
  return String(text ?? '').includes(EBAY_PRINT_LINE)
}

const COPY_PASTE_RE = /\bcopy\s+(?:and|&)\s+paste\b/i

// The reply with EBAY_PRINT_LINE after the last label link (anchors: the
// label URLs, or the restore tokens / INSERTLABELLINK that stand for them),
// after the template's copy-and-paste sentence when that follows the link.
// Unchanged when the line is already there or no anchor is found.
export function withEbayPrintLine(text, anchors = []) {
  const t = String(text ?? '')
  if (!t || hasEbayPrintLine(t)) return t
  const list = (anchors ?? []).filter(Boolean)
  let at = -1
  let anchor = null
  for (const a of list) {
    const i = t.lastIndexOf(a)
    if (i > at) { at = i; anchor = a }
  }
  if (at < 0) return t
  if (isHtml(t)) {
    const end = at + anchor.length
    return `${t.slice(0, end)}<br>${EBAY_PRINT_LINE}${t.slice(end)}`
  }
  const lines = t.split('\n')
  let pos = 0
  let idx = 0
  for (; idx < lines.length; idx++) {
    if (pos + lines[idx].length >= at) break
    pos += lines[idx].length + 1
  }
  let k = idx
  // The template's copy-and-paste sentence right after the link stays with it.
  let j = k + 1
  while (j < lines.length && !lines[j].trim()) j++
  if (j < lines.length && j <= k + 2 && COPY_PASTE_RE.test(lines[j])) k = j
  lines.splice(k + 1, 0, EBAY_PRINT_LINE)
  return lines.join('\n')
}

// The reply without EBAY_PRINT_LINE (its own line, or after a <br>).
export function withoutEbayPrintLine(text) {
  const t = String(text ?? '')
  if (!hasEbayPrintLine(t)) return t
  return t
    .split(`<br>${EBAY_PRINT_LINE}`).join('')
    .split('\n').filter((l) => l.trim() !== EBAY_PRINT_LINE).join('\n')
    .split(EBAY_PRINT_LINE).join('')
}

// Whether a processed step's result says its label did NOT go onto the eBay
// return (attachedToEbayReturn false). results: the executor's
// [{ success, result }] or bare results.
export function ebayAttachFailedIn(results = []) {
  return (results ?? []).some((r) => (r?.result ?? r)?.attachedToEbayReturn === false)
}

// The reply after Process: the eBay print line removed when a step's attach
// did not happen. Unchanged otherwise.
export function replyAfterEbayAttach(reply, results = []) {
  return ebayAttachFailedIn(results) ? withoutEbayPrintLine(reply) : reply
}
